import { MenuItem } from "@material-ui/core";
import React, { ReactNode, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "underscore";
import DataTable from "../../../components/DataTable/DataTable";
import { IDataTableRow } from "../../../components/DataTable/DataTableBody/DataTableBody";
import { useDataTableContext } from "../../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FilterRef } from "../../../components/DataTable/DataTableFilters/DataTableFilters";
import { Column, Order } from "../../../components/DataTable/DataTableHead/DataTableHead";
import {
  DataTableActions,
  DataTableFiltering,
  DataTableHeadingProps,
} from "../../../components/DataTable/DataTableHeading/DataTableHeading";
import useAppContext from "../../../context/hooks/useAppContext";
import useFleetManagerContext from "../../../context/hooks/useFleetManagerContext";
import { ParkingProductType } from "../../../models/ParkingProductModel";
import { useAppDispatch, useAppSelector } from "../../../store";
import * as entryRightsActions from "../../../store/actions/entryRights.actions";
import parkingProductsActions from "../../../store/actions/parkingProducts.actions";
import { ForbiddenIconMenu } from "../../../ui/Icons/ForbiddenIcon";
import { TruncatedTextProps } from "../../../components/TruncatedText/TruncatedText";
import "./_fleetManagerTable.scss";
import {
  DeleteDelegeeIcon,
  DeleteDelegeeMenuItem,
  EditIcon,
  NoPaddingGrid,
  ResendInvitationIcon,
} from "./StyledComponents";
import { SelectedDelegeeRowKey } from "../../../context/store/actions/fleetManagerContext.types";
import { isEntryRightsMyProductsRowKey, selectEnableDelegationsScheduling } from "../MyProducts/ParkingRightsDataTable/common";

export interface IFleetManagerDataTableModel extends IDataTableRow {
  name: JSX.Element;
  email: React.ReactElement<TruncatedTextProps>;
  invitationDate: string;
  remainingFlex?: string;
  status: JSX.Element;
}

interface IFleetManagerDataTable {
  columns: Column<IFleetManagerDataTableModel>[];
  rows: IFleetManagerDataTableModel[];
  heading: DataTableHeadingProps;
  actions: DataTableActions;
  filtering: DataTableFiltering;
  totalPages: number;
  isLoading: boolean;
  orderBy: string;
  order: Order;
  hideWhenNoData?: boolean;
  hideActionsMenu?: boolean;
  onSort?: (
    event: React.MouseEvent<unknown>,
    property: keyof IFleetManagerDataTableModel
  ) => void;
  getRegistrationIdFromTableRow: (
    focusedRow: IDataTableRow | null
  ) => string | null;
  isResendInviteDisabled: (focusedRow: IDataTableRow | null) => boolean;
  resendAll: boolean;
  onFilterClicked?: () => void;
  onResetFiltersClicked?: () => void;
  translationOverrideKey: string;
  subHeading?: ReactNode;
  productType?: ParkingProductType
}

function useRowActions({ productType }: { productType?: ParkingProductType }) {
    const dispatch = useAppDispatch();
    const {
        togglePedestrianEditForm,
        setSelectedDelegeeId,
        setSelectedDelegeeRowKey,
        toggleRevokeParkingRightsWarning,
        toggleEditDelegee,
        toggleDeleteDelegeeWarning,
    } = useFleetManagerContext();
    const resendDelegeeInvite = (
        entryRightId: string,
        registrationId: string,
        seasonTicketOwnerCrmId: string,
        email: string,
        resendAll: boolean
    ) => {
        if (resendAll) {
          dispatch(
            entryRightsActions.resendAllInvitesActionThunk({
                email,
                customerId: seasonTicketOwnerCrmId,
            })
          );
          return;
        }
        if (productType === ParkingProductType.PedestrianAccess) {
            dispatch(
                entryRightsActions.resendInviteActionThunk({
                    email,
                    customerId: seasonTicketOwnerCrmId,
                    entryRightId: entryRightId,
                })
            );
            return;
        }
        dispatch(
            parkingProductsActions.resendDelegeeInvite(registrationId, seasonTicketOwnerCrmId, email, entryRightId)
        );
    };

    const revoke = (delegeeId: string, rowKey: SelectedDelegeeRowKey) => {
        if (productType === ParkingProductType.PedestrianAccess) {
            setSelectedDelegeeId(delegeeId);
            setSelectedDelegeeRowKey(rowKey);
            dispatch(entryRightsActions.toggleShowRevokeEntryRightsAction());
            return;
        }
        setSelectedDelegeeId(delegeeId);
        setSelectedDelegeeRowKey(rowKey);
        toggleRevokeParkingRightsWarning();
    };

    const edit = (delegeeId: string) => {
        if (productType === ParkingProductType.PedestrianAccess) {
            togglePedestrianEditForm(delegeeId);
            return;
        }
        toggleEditDelegee(delegeeId);
    }

    const deleteDelegee = (delegeeId: string) => {
        if (productType === ParkingProductType.PedestrianAccess) {
            setSelectedDelegeeId(delegeeId);
            dispatch(entryRightsActions.toggleShowDeleteEntryRightsDelegeesAction())
            return;
        }
        setSelectedDelegeeId(delegeeId);
        toggleDeleteDelegeeWarning();
    }
    return {
        resendDelegeeInvite,
        revoke,
        edit,
        deleteDelegee,
    };
};

const FleetManagerDataTable = (props: IFleetManagerDataTable) => {
  const {
    columns,
    rows,
    heading,
    filtering,
    actions,
    totalPages,
    isLoading,
    orderBy,
    order,
    onSort,
    getRegistrationIdFromTableRow,
    isResendInviteDisabled,
    resendAll,
    onFilterClicked,
    onResetFiltersClicked,
    hideWhenNoData,
    hideActionsMenu,
    translationOverrideKey,
    subHeading,
    productType
  } = props;
  const { t } = useTranslation(["fleetManagerTable", "globals", "dataTable"]);
  const {
    fleetManagerState,
    setSelectedParkingRightIds
  } = useFleetManagerContext();
  const { appState } = useAppContext();
  const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
  const { state } = useDataTableContext();
  const filtersRef = useRef<FilterRef>({
    onFilterClicked: () => setSelectedParkingRightIds([]),
  } as FilterRef);

  const rowActions = useRowActions({ productType: productType });

  const handleEditClick = (focusedRow: IDataTableRow | null) => {
    const registrationId = getRegistrationIdFromTableRow(focusedRow) || "";
    rowActions.edit(registrationId);
  };

  const handleDeleteClick = (focusedRow: IDataTableRow | null) => {
    const registrationId = getRegistrationIdFromTableRow(focusedRow) || "";
    rowActions.deleteDelegee(registrationId);
  };

  const handleRevokeClick = (focusedRow: IDataTableRow | null) => {
    const registrationId = getRegistrationIdFromTableRow(focusedRow) || "";
    rowActions.revoke(registrationId, focusedRow?.rowKey || "");
  };

  const handleResendInviteClick = (
    focusedRow: IDataTableRow | null,
    handleMenuClose: () => void
  ) => {
    const registrationId = getRegistrationIdFromTableRow(focusedRow) || "";
    const email = focusedRow?.email.props.text || "";
    const rowKey = focusedRow?.rowKey;
    const entryRightId = isEntryRightsMyProductsRowKey(rowKey)
      ? rowKey.entryRightId || ""
      : (rowKey as string) || "";
    rowActions.resendDelegeeInvite(
      entryRightId,
      registrationId,
      appState.user.seasonTicketOwnerCrmId as string,
      email,
      resendAll
    );
    handleMenuClose();
  };

  const renderMenuItems = (
    focusedRow: IDataTableRow | null,
    onClose: () => void
  ): JSX.Element[] => {
    const editItem = (
      <MenuItem key="edit" onClick={() => handleEditClick(focusedRow)}>
        <EditIcon />
        {t("contextMenuActions.editDelegee")}
      </MenuItem>
    );
    const resendInviteItem = (
      <MenuItem
        key="resend"
        onClick={() => handleResendInviteClick(focusedRow, onClose)}
        disabled={isResendInviteDisabled(focusedRow)}
      >
        <ResendInvitationIcon />
        {t("contextMenuActions.resendInvite")}
      </MenuItem>
    );
    const revokeItem = (
      <MenuItem
        key="revoke"
        onClick={() => handleRevokeClick(focusedRow)}
        disabled={!(focusedRow?.products || focusedRow?.product)}
      >
        <ForbiddenIconMenu />
        {enableDelegationsScheduling
          ? t("contextMenuActions.revokeProduct")
          : t("contextMenuActions.revokeDelegee")}
      </MenuItem>
    );
    const deleteItem = (
      <DeleteDelegeeMenuItem
        key="delete"
        onClick={() => handleDeleteClick(focusedRow)}
      >
        <DeleteDelegeeIcon />
        {t("contextMenuActions.deleteDelegee")}
      </DeleteDelegeeMenuItem>
    );

    const menuItems = [editItem, resendInviteItem, revokeItem, deleteItem];

    return menuItems;
  };

  const filtersHaveChanged = useCallback(() => {
    state.filters?.forEach((filter) => {
      if (!isEmpty(filter.value)) {
        setSelectedParkingRightIds([]);
      }
    });
  }, [state.filters]);

  useEffect(() => {
    filtersHaveChanged();
  }, [filtersHaveChanged]);

  return (
    <NoPaddingGrid container item direction="row" xs={12}>
      <NoPaddingGrid container item direction="column" xs={12}>
        <DataTable
            filtersRef={filtersRef}
            columns={columns}
            rows={rows}
            renderActionMenu={renderMenuItems}
            loading={isLoading}
            heading={heading}
            filtering={filtering}
            onFilterClicked={onFilterClicked}
            onResetFiltersClicked={onResetFiltersClicked}
            actions={actions}
            orderBy={orderBy}
            order={order}
            onRequestSort={onSort}
            pages={totalPages}
            selectedRowKeys={fleetManagerState.selectedRowKeys}
            setSelectedRowKeys={setSelectedParkingRightIds}
            hideWhenNoData={hideWhenNoData}
            hideActionsMenu={hideActionsMenu}
            translationOverrideKey={translationOverrideKey}
            subHeading={subHeading}
        />
      </NoPaddingGrid>
    </NoPaddingGrid>
  );
};

export default FleetManagerDataTable;
